<template>
    <div class="integrations-list-container">
        <!-- Tabela de logs -->
        <div class="logs-table">
            <div class="card shadow-sm border-0">
                <div class="card-header bg-gradient-dark text-white">
                    <h6 class="card-title mb-0">
                        <i class="fas fa-list mr-2"></i>
                        {{ reserveId ? 'Logs de Integração da Reserva' : 'Logs de Integração' }}
                        <small
                            v-if="loading"
                            class="ml-2"
                        >
                            <i class="fas fa-spinner fa-spin"></i>
                            Carregando...
                        </small>
                    </h6>
                </div>
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <base-crud
                            v-if="tableReady"
                            ref="baseCrudTable"
                            :table="table"
                            :extra="extra"
                            :columns="columns"
                            :options="options"
                            :endPoint="endPoint"
                            :enableAdd="false"
                            :enableEdit="false"
                            :enableDelete="false"
                            :enableView="false"
                            :dataForm="dataForm"
                            @viewReservation="viewLogDetails"
                            @retryIntegration="retryFromTable"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de detalhes -->
        <ModalRequest
            :modal-visualizar-log="modalVisualizarLog"
            :selected-log="selectedLog"
            :reservation-details="reservationDetails"
            :loading-reservation="loadingReservation"
            :copied-states="copiedStates"
            :active-tab="activeTab"
            :retry-loading="retryLoading"
            :loading-stats="false"
            :stats-data="{}"
            @close="fecharModal"
            @update-active-tab="updateActiveTab"
            @load-reservation="loadReservationData"
            @retry-integration="retryIntegration"
            @copy-to-clipboard="copyToClipboard"
        />
    </div>
</template>

<script>
import BaseCrud from "../Base/BaseCrud"
import ModalRequest from "./ModalRequest.vue"
import http from "../../http"
import {
    formatExecutionTime,
    getErrorTypeClass,
    getErrorTypeLabel,
    getHttpStatusClass,
    getMethodLabel,
    getStatusBadgeClass,
    getStatusLabel
} from "../../composables/mask"

export default {
    name: 'IntegrationsList',

    components: {
        BaseCrud,
        ModalRequest
    },

    props: {
        reserveId: {
            type: [String, Number],
            default: null
        },
        autoLoad: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            filtersExpanded: false,
            tableReady: false,
            loading: false,
            modalVisualizarLog: false,
            retryLoading: false,
            loadingReservation: false,

            activeTab: 'log',

            selectedLog: null,
            reservationDetails: null,

            copiedStates: {
                request: false,
                response: false
            },

            filters: {
                entity_id: '',
                status: '',
                method: '',
                error_type: '',
                http_status: ''
            },

            extra: [
                {
                    id: 1,
                    label: "",
                    class: "fas fa-eye btn btn-crud view",
                    title: "Ver detalhes do log",
                    name: "viewReservation",
                    icon: "btn btn-crud view",
                }
            ],

            table: "integrationLogsTable",
            columns: [
                "id",
                "entity_id",
                "method_name",
                "status",
                "error_type",
                "http_status_code",
                "execution_time",
                "created_at",
                "actions"
            ],
            endPoint: "integration-logs/",
            dataForm: {},

            filterTimeout: null,
        }
    },

    computed: {
        url() {
            return this.$store.getters.api + this.endPoint
        },

        options() {
            return {
                sortable: [
                    "id",
                    "method_name",
                    "status",
                    "created_at"
                ],
                filterable: false,
                filterByColumn: true,
                debounce: 1000,
                pagination: {
                    chunk: 10,
                    dropdown: false,
                    nav: "scroll"
                },
                perPage: 10,
                perPageValues: [10, 25, 50],
                headings: {
                    id: "ID",
                    entity_id: "Matrícula/Reserva",
                    method_name: "Método",
                    status: "Status",
                    error_type: "Tipo de Erro",
                    http_status_code: "Código HTTP",
                    execution_time: "Tempo",
                    created_at: "Data/Hora",
                    actions: "Ações",
                },
                templates: {
                    status: (h, row) => {
                        const statusClass = getStatusBadgeClass(row.status);
                        return h('span', {
                            attrs: {class: `px-2 py-1 text-xs font-medium rounded-full ${statusClass}`}
                        }, getStatusLabel(row.status));
                    },
                    error_type: (h, row) => {
                        if (!row.error_type) {
                            return h('span', {
                                attrs: {class: 'text-muted'}
                            }, '-');
                        }
                        const errorClass = getErrorTypeClass(row.error_type);
                        return h('span', {
                            attrs: {class: `px-2 py-1 text-xs font-medium rounded ${errorClass}`}
                        }, getErrorTypeLabel(row.error_type));
                    },
                    method_name: (h, row) => {
                        return h('span', {}, getMethodLabel(row.method_name));
                    },
                    http_status_code: (h, row) => {
                        if (!row.http_status_code) {
                            return h('span', {
                                attrs: {class: 'text-muted'}
                            }, '-');
                        }
                        const statusClass = getHttpStatusClass(row.http_status_code);
                        return h('span', {
                            attrs: {class: statusClass}
                        }, row.http_status_code);
                    },
                    execution_time: (h, row) => {
                        return h('span', {}, formatExecutionTime(row.execution_time));
                    }
                },
                requestFunction: (data) => {
                    if (!data || !data.query) {
                        data = {
                            query: {},
                            page: 1,
                            limit: 10,
                            orderBy: '',
                            ascending: true
                        };
                    }

                    let query = this.buildQuery(data.query || {});

                    const customQuery = this.buildCustomFiltersQuery();

                    let requestData = {
                        params: Object.assign({}, data, {
                            query: "",
                            with: []
                        })
                    };

                    const fullUrl = this.url + "?" + query + customQuery;

                    return http.get(fullUrl, requestData).catch((e) => {
                        console.error('Erro na requisição da tabela:', e);
                        this.$store.dispatch("error", e);
                        return {
                            data: {
                                data: [],
                                total: 0,
                                current_page: 1,
                                last_page: 1,
                                per_page: 10
                            }
                        };
                    });
                },
            }
        }
    },

    mounted() {
        this.initializeTable();
    },

    methods: {
        /**
         * Inicializa a tabela / Initialize table
         */
        initializeTable() {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.tableReady = true;
                }, 100);
            });
        },

        /**
         * Limpa os dados da tabela de forma segura / Safely clear table data
         */
        clearTableData() {
            try {
                if (this.$refs.baseCrudTable &&
                    this.$refs.baseCrudTable.$refs.table &&
                    this.$refs.baseCrudTable.$refs.table.data) {
                    this.$refs.baseCrudTable.$refs.table.data = [];
                }
            } catch (error) {
                console.warn('Erro ao limpar dados da tabela:', error);
            }
        },

        /**
         * Atualiza a tabela de forma segura / Safely refresh table
         */
        async refreshTable() {
            try {
                if (this.$refs.baseCrudTable &&
                    this.$refs.baseCrudTable.$refs.table &&
                    typeof this.$refs.baseCrudTable.$refs.table.refresh === 'function') {

                    await this.$refs.baseCrudTable.$refs.table.refresh();
                    return true;
                }
            } catch (error) {
                console.warn('Erro ao atualizar tabela:', error);
            }
            return false;
        },

        /**
         * Aplica os filtros / Apply filters
         */
        async applyFilters(filters) {
            this.loading = true;

            try {
                this.filters = filters;
                this.clearTableData();

                await new Promise(resolve => setTimeout(resolve, 200));

                await this.refreshTable();

            } catch (error) {
                console.error('Erro ao aplicar filtros:', error);
            } finally {
                setTimeout(() => {
                    this.loading = false;
                }, 500);
            }
        },

        /**
         * Constrói query string para filtros customizados / Build query string for custom filters
         */
        buildCustomFiltersQuery() {
            let query = '';

            if (this.reserveId) {
                query += `&entity_id=${encodeURIComponent(this.reserveId)}&entity_name=reserve_vehicles.id`;
            }

            Object.keys(this.filters).forEach(key => {
                if (this.filters[key]) {
                    query += `&${key}=${encodeURIComponent(this.filters[key])}`;
                }
            });

            return query;
        },

        /**
         * Constrói a query string para filtros padrão / Build query string for default filters
         */
        buildQuery(queryFilters = {}) {
            let columns = {
                id: "id",
                method_name: "method_name",
                status: "status",
                error_type: "error_type",
            };
            let filters = "";

            if (queryFilters && typeof queryFilters === 'object') {
                Object.keys(queryFilters).forEach(index => {
                    if (columns[index] && queryFilters[index]) {
                        filters += columns[index] + "=" + encodeURIComponent(queryFilters[index]) + "&";
                    }
                });
            }

            return filters;
        },

        /**
         * Abre modal com detalhes do log / Open modal with log details
         */
        async viewLogDetails(props, row, index) {
            try {
                const logData = row || props;

                if (!logData) {
                    return;
                }

                this.selectedLog = logData.row;
                this.activeTab = 'log';
                this.reservationDetails = null;

                this.copiedStates = {
                    request: false,
                    response: false
                };

                this.modalVisualizarLog = true;

            } catch (error) {
                console.error('Erro ao carregar detalhes do log:', error);
            }
        },

        /**
         * Atualiza a aba ativa / Update active tab
         */
        updateActiveTab(tab) {
            this.activeTab = tab;
        },

        /**
         * Carrega dados da reserva quando a aba é clicada / Load reservation data when tab is clicked
         */
        async loadReservationData() {
            if (!this.selectedLog || !this.selectedLog.entity_id) {
                return;
            }

            this.activeTab = 'reservation';

            if (this.reservationDetails) {
                return;
            }

            this.loadingReservation = true;

            try {
                const apiUrl = this.$store.getters.api + "reserveVehicles/" + this.selectedLog.entity_id;

                const response = await http.get(apiUrl);
                this.reservationDetails = response.data;
            } catch (error) {
                console.error('Erro ao carregar dados da reserva:', error);
                this.reservationDetails = null;
            } finally {
                this.loadingReservation = false;
            }
        },

        /**
         * Retry da integração a partir da tabela / Retry integration from table
         */
        async retryFromTable(props, row, index) {
            this.selectedLog = props.row || row;
            await this.retryIntegration();
        },

        /**
         * Tenta realizar a integração novamente / Try integration again
         */
        async retryIntegration() {
            if (!this.selectedLog) return;

            this.retryLoading = true;

            try {
                const apiUrl = this.$store.getters.api + `integration-logs/retry/${this.selectedLog.id}`;

                const response = await http.post(apiUrl, {});

                this.fecharModal();

                await this.$nextTick();
                await this.refreshTable();

                this.$emit('integration-retried', response.data);

            } catch (error) {
                console.error('Erro ao tentar integração novamente:', error);
            } finally {
                this.retryLoading = false;
            }
        },

        /**
         * Copia dados para clipboard / Copy data to clipboard
         */
        async copyToClipboard(data, type) {
            try {
                if (!data) {
                    return;
                }

                const jsonString = JSON.stringify(data, null, 2);

                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(jsonString);
                } else {
                    const textArea = document.createElement("textarea");
                    textArea.value = jsonString;
                    textArea.style.position = 'fixed';
                    textArea.style.opacity = '0';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }

                this.copiedStates[type] = true;

                setTimeout(() => {
                    this.copiedStates[type] = false;
                }, 2000);

            } catch (error) {
                console.error('Erro ao copiar para clipboard:', error);
            }
        },

        /**
         * Fecha o modal de detalhes / Close details modal
         */
        fecharModal() {
            this.modalVisualizarLog = false;
            this.selectedLog = null;
            this.reservationDetails = null;
            this.retryLoading = false;
            this.loadingReservation = false;
            this.activeTab = 'log';
            this.copiedStates = {request: false, response: false};
        },

        /**
         * Método público para recarregar a tabela / Public method to reload table
         */
        async reloadTable() {
            if (this.tableReady) {
                await this.refreshTable();
            }
        }
    }
}
</script>

<style scoped>
.integrations-list-container {
    background-color: white;
}

/* Seção de filtros / Filters section */
.filters-section .card {
    border: none;
    border-radius: 12px;
    overflow: hidden;
}

.form-label {
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 8px;
    border: 1px solid #d1d5db;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
    border-color: #fd7e14;
    box-shadow: 0 0 0 0.2rem rgba(255, 123, 37, 0.25);
}

/* Estilos da tabela / Table styles */
.logs-table .card {
    border: none;
    border-radius: 12px;
    overflow: hidden;
}

.table-responsive {
    border-radius: 0 0 12px 12px;
}

/* Botões / Buttons */
.btn {
    font-weight: 600;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.btn-primary {
    background: linear-gradient(90deg, #fd7e14 0%, #e55100 100%);
    border: none;
    color: white;
}

.btn-primary:hover {
    background: linear-gradient(90deg, #e55100 0%, #fd7e14 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 123, 37, 0.4);
}

.btn-secondary {
    background: linear-gradient(90deg, #6c757d 0%, #495057 100%);
    border: none;
    color: white;
}

.btn-secondary:hover {
    background: linear-gradient(90deg, #495057 0%, #6c757d 100%);
    transform: translateY(-1px);
}

/* Responsividade / Responsiveness */
@media (max-width: 768px) {
    .filters-section .card-body {
        padding: 1rem;
    }

    .form-control {
        font-size: 0.8rem;
        padding: 0.4rem 0.6rem;
    }

    .btn {
        font-size: 0.8rem;
        padding: 0.4rem 0.8rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/IntegrationLog/IntegrationsList.vue